import api from '@shared/services/api.js';
import { getDayAndDateAndTime } from '@shared/utils/converters.js';
import { onErrorHandler } from '@shared/utils/errorHandlers.js';
import { createCsvContent } from '@src/shared/utils/csv';

export async function getRates(tableOptions = {}, configuration_id = null) {
  let rates = {};
  const url = api.constructUrl('/pe/api/rates/', { ...tableOptions });
  const response = await api.get(url);
  rates = response.rates;
  if (configuration_id) {
    rates = response.rates.filter(
      rate => rate.changesetId === configuration_id,
    );
  }
  rates.some(function (item) {
    if (item.isPublished) {
      item._rowVariant = 'active';
      return true;
    }
  });
  if (response.next) {
    tableOptions.total = rates.length + 1;
  }
  return rates;
}

/**
 * Generates base rates from the chosen investor rate sets. When the session
 * confirmed a cash flow solve batch, its id is sent too, so this pricing can
 * later pin a re-run to that batch's benchmark values. The past picks go with
 * it so they are not linked to this session: their pricing came from an
 * earlier one.
 */
export async function generateBaseRates(
  configuration_id,
  sourceRateSetIds,
  cashFlowSolveBatchId = null,
  pastRateSetIdsByInvestor = {},
) {
  const result = await api.post('/pe/api/rates/', {
    configuration_id: configuration_id,
    source_rate_set_ids: sourceRateSetIds,
    ...(cashFlowSolveBatchId && {
      cash_flow_solve_batch_id: cashFlowSolveBatchId,
      past_rate_set_ids_by_investor: pastRateSetIdsByInvestor,
    }),
  });
  return result;
}

export const getBaseRateRequest = async (orgId, baseRateSetId) => {
  const result = await api.get(
    `/api/v1/organizations/${orgId}/base_rate_requests/${baseRateSetId}/`,
  );
  return result;
};

export async function refreshAgencyInvestorRates(
  agencyInvestorId,
  configurationId,
) {
  return api.post('/pe/api/investor-rates', {
    agency_investor_id: agencyInvestorId,
    configuration_id: configurationId,
  });
}

/**
 * Request generation of the Custom Products rate set for a version and return
 * the new set's id and creation time. A backend warning rejects instead of
 * resolving.
 */
export async function generateCustomProductsRates(configurationId) {
  // Without raiseWarnings the interceptor swallows a warning's rejection and this resolves undefined.
  const response = await api.post(
    '/pe/api/investor-rates/custom-products',
    { configuration_id: configurationId },
    { raiseWarnings: true },
  );
  return response.result;
}

export async function getRecentInvestorRateInfo(investorId) {
  return api.get(`/pe/api/investor-rates/${investorId}/recent/`);
}

export async function getInvestorRates(investorRateSetId) {
  try {
    const response = await api.get(
      `/pe/api/investor-rates/${investorRateSetId}`,
    );
    return response.investor_rates;
  } catch (error) {
    onErrorHandler(error, 'pe-get-investor_rates_set');
  }
}

export async function downloadInvestorRates(investorRateSetId) {
  const investorRates = await getInvestorRates(investorRateSetId);
  const dateTime = getDayAndDateAndTime(investorRates.createdOn).replace(
    /\s/g,
    '',
  );
  const downloadFileName = `${investorRates.investor}_${dateTime}.csv`;
  const csv = investorRatesToCSV(investorRates);

  const anchorElement = document.createElement('a');
  anchorElement.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
  anchorElement.target = '_blank';
  anchorElement.download = downloadFileName;
  anchorElement.click();
}

export function investorRatesToCSV(investorRates) {
  const header = {
    productCode: 'Product',
    productName: 'Name',
    rate: 'Rate',
    price: 'Price',
    lockPeriod: 'Lock',
    coupon: 'Coupon',
  };

  const hasSSV = investorRates.rates.some(r => r.servicingStrip !== 0);
  if (hasSSV) {
    header.servicingStrip = 'Servicing Strip';
  }

  return createCsvContent(
    [
      header,
      ...investorRates.rates.map(r => {
        const row = {
          productCode: r.productCode,
          productName: r.productName,
          rate: r.rate,
          price: r.price,
          lockPeriod: r.lockPeriod,
          coupon: r.coupon,
        };
        if (hasSSV) {
          row.servicingStrip = r.servicingStrip;
        }
        return row;
      }),
    ],
    { newlineSeparator: '\n' },
  );
}

export async function uploadCustomRateSheet(customRateSheetFile) {
  const formData = new FormData();
  formData.append('customRates', customRateSheetFile, customRateSheetFile.name);
  const response = await api.postFormData('/pe/api/custom-rates', formData);
  return response.data.result;
}

export async function getMbsOverrideTransactions() {
  try {
    const response = await api.get(`/api/mbs-overrides/transactions`);
    return response;
  } catch (error) {
    onErrorHandler(error, 'pe-get-mbs-override-transactions');
  }
}

export async function getMbsTransactionOverrides(transactionId) {
  try {
    const response = await api.get(
      `/api/mbs-overrides/transactions/${transactionId}`,
    );
    return response;
  } catch (error) {
    onErrorHandler(error, 'pe-get-mbs-transaction-overrides');
  }
}

export async function getMbsBenchmarksInfo(changesetId) {
  try {
    const response = await api.get(`/api/mbs-benchmarks/${changesetId}/info`);
    return response;
  } catch (error) {
    onErrorHandler(error, 'pe-get-mbs-benchmarks-info');
  }
}
